import { useState, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/context/AuthContext";
import { useAutosave } from "@/hooks/useAutosave";
import api, { apiErr, isPaywall } from "@/lib/api";
import { MicButton } from "@/components/MicButton";
import { Container, PageHeader, Spinner, PaywallNotice, GenerateButton } from "@/components/shared";
import ReplyResult from "@/components/ReplyResult";
import RemainingCounter from "@/components/RemainingCounter";
import { toast } from "sonner";
import { Sparkles, ScanLine, Upload, X, Camera, Image as ImageIcon } from "lucide-react";

export default function Reply() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const isChild = user?.profile === "child";
  const [params] = useSearchParams();
  const [comment, setComment] = useState("");
  useAutosave("reply", comment, setComment);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [showScan, setShowScan] = useState(false);
  const [preview, setPreview] = useState(null);
  const [paywall, setPaywall] = useState(false);
  const [usageTrigger, setUsageTrigger] = useState(0);
  const [showChoice, setShowChoice] = useState(false);
  const fileRef = useRef(null);
  const cameraRef = useRef(null);

  useEffect(() => {
    const c = params.get("comment");
    if (c) setComment(c);
    if (params.get("mode") === "scan") setShowScan(true);
    if (c && params.get("auto") === "1") generate(c);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  const generate = async (text) => {
    const c = (typeof text === "string" ? text : comment).trim();
    if (!c) return;
    setBusy(true); setPaywall(false); setResult(null);
    try {
      const { data } = await api.post("/ai/reply", { comment: c });
      setResult(data);
      setUsageTrigger((t) => t + 1);
    } catch (e) {
      if (isPaywall(e)) setPaywall(true);
      else toast.error(apiErr(e));
    } finally { setBusy(false); }
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    setShowChoice(false);
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async () => {
      const b64 = reader.result;
      setPreview(b64);
      setScanning(true); setPaywall(false); setResult(null);
      try {
        const { data } = await api.post("/ai/scan", { image_base64: b64 });
        if (data.comment) setComment(data.comment);
        setResult(data);
        setUsageTrigger((t) => t + 1);
        toast.success(t("pages.reply.textDetected"));
      } catch (err) {
        if (isPaywall(err)) setPaywall(true);
        else toast.error(apiErr(err));
      } finally { setScanning(false); }
    };
    reader.readAsDataURL(file);
  };

  return (
    <Container>
      <PageHeader eyebrow={t("pages.reply.eyebrow")} title={t("pages.reply.title")}
        subtitle={t("pages.reply.subtitle")} />

      <div className="mb-4"><RemainingCounter kind="reply" trigger={usageTrigger} /></div>

      <div className="card-surface p-5 mb-6">
        <div className="flex items-center justify-between mb-3">
          <span className="text-white/40 text-sm">{t("pages.reply.yourComment")}</span>
          <button data-testid="toggle-scan" onClick={() => setShowScan((s) => !s)}
            className="inline-flex items-center gap-2 text-sm text-gold hover:opacity-80">
            {showScan ? <X size={15} /> : <ScanLine size={15} />} {showScan ? t("pages.reply.closeScan") : t("pages.reply.scanImage")}
          </button>
        </div>

        {showScan && (
          <div data-testid="scan-zone" className="mb-4 rounded-xl border border-dashed border-gold/40 bg-white/[0.02] p-5 text-center">
            {/* Caméra (capture) et galerie/photothèque (sans capture) */}
            <input ref={cameraRef} type="file" accept="image/*" capture="environment" onChange={onFile} className="hidden" data-testid="scan-input-camera" />
            <input ref={fileRef} type="file" accept="image/*" onChange={onFile} className="hidden" data-testid="scan-input" />
            {preview ? (
              <img src={preview} alt="aperçu" className="max-h-40 mx-auto rounded-lg mb-3" />
            ) : (
              <ScanLine size={28} className="text-gold mx-auto mb-2" />
            )}
            <p className="text-white/50 text-sm mb-3">{t("pages.reply.scanHint")}</p>
            {!showChoice ? (
              <button data-testid="scan-upload-button" onClick={() => setShowChoice(true)} disabled={scanning}
                className="inline-flex items-center gap-2 border border-white/20 rounded-full px-4 py-2 text-sm hover:bg-white/[0.06] disabled:opacity-50">
                <Upload size={15} /> {scanning ? t("pages.reply.analyzing") : t("pages.reply.addImage")}
              </button>
            ) : (
              <div className="flex flex-col sm:flex-row items-center justify-center gap-2" data-testid="scan-choice">
                <button data-testid="scan-choice-camera" onClick={() => { setShowChoice(false); cameraRef.current?.click(); }} disabled={scanning}
                  className="inline-flex items-center gap-2 border border-white/20 rounded-full px-4 py-2 text-sm hover:bg-white/[0.06] disabled:opacity-50">
                  <Camera size={15} /> {t("pages.reply.takePhoto")}
                </button>
                <button data-testid="scan-choice-gallery" onClick={() => { setShowChoice(false); fileRef.current?.click(); }} disabled={scanning}
                  className="inline-flex items-center gap-2 border border-white/20 rounded-full px-4 py-2 text-sm hover:bg-white/[0.06] disabled:opacity-50">
                  <ImageIcon size={15} /> {t("pages.reply.chooseGallery")}
                </button>
              </div>
            )}
          </div>
        )}

        <textarea data-testid="reply-input" rows={4} value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder={isChild ? t("pages.reply.placeholderChild") : t("pages.reply.placeholder")}
          className="w-full bg-transparent outline-none resize-none text-white placeholder:text-white/30" />
        <div className="flex items-center justify-between gap-3 mt-3">
          <MicButton testid="reply-input-mic" value={comment} onChange={setComment} />
          <GenerateButton data-testid="reply-generate-button" onClick={generate} disabled={busy || !comment.trim()}>
            <Sparkles size={16} /> {t("pages.reply.generate")}
          </GenerateButton>
        </div>
      </div>

      {(busy || scanning) && <Spinner label={scanning ? t("pages.reply.spinnerScan") : t("pages.reply.spinnerGen")} />}
      {paywall && <PaywallNotice />}

      {result && <ReplyResult data={result} idPrefix="reply" />}
    </Container>
  );
}
